import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  BellOff,
  ExternalLink,
  Pencil,
  Phone,
  PhoneCall,
  Tag,
  Trash2,
  UserCheck,
} from 'lucide-react';
import { createPortal } from 'react-dom';

import { TagBadge } from '@/components/contacts/TagBadge';
import {
  useContact,
  useContactTags,
  useToolWatchLastCleared,
  useUpdateContact,
} from '@/hooks/useContacts';
import {
  useClaimConversation,
  useConversation,
  useUnclaimConversation,
} from '@/hooks/useMessaging';
import {
  useCallEligibility,
  useCheckWhatsAppPermission,
  useRequestWhatsAppPermission,
} from '@/hooks/useOperatorCall';
import { useTagDefinitions } from '@/hooks/useTagDefinitions';
import { clearedByLabel } from '@/lib/message-time';
import { useAuthStore } from '@/stores/auth.store';

import { pickReplyConversation } from './reply-conversation';

import { mergeTagsWithTypes } from '@repo/shared-types';

interface ContactContextMenuProps {
  contactId: string;
  position: { x: number; y: number };
  onClose: () => void;
  /** Pre-known from ChatCard — avoids fetching conversations to resolve it. */
  conversationId?: string;
  /** Pre-known tags from the list view (ContactWithCounts). */
  initialTags?: string[];
  /** Called when the user selects a call option — opens the call dialog with the channel pre-selected. */
  onCall?: (channel: 'pstn' | 'whatsapp', overridePermission?: boolean) => void;
  /** Opens the edit-contact dialog (host owns the dialog). */
  onEdit?: () => void;
  /** Opens the delete-contact confirmation (host owns the dialog). */
  onDeleteContact?: () => void;
}

export const ContactContextMenu = ({
  contactId,
  position,
  onClose,
  conversationId: preknownConversationId,
  initialTags,
  onCall,
  onEdit,
  onDeleteContact,
}: ContactContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [now] = useState(() => Date.now());

  const currentUser = useAuthStore((s) => s.user);
  const { data: contact } = useContact(contactId);
  const { data: allTags } = useContactTags();
  const { definitions, byName } = useTagDefinitions();
  const updateContact = useUpdateContact();
  const claimConversation = useClaimConversation();
  const unclaimConversation = useUnclaimConversation();

  // Resolve conversation ID from the contact's WhatsApp conversation
  const conversations = contact?.messaging_conversations ?? [];
  const whatsappConv = pickReplyConversation(conversations, 'WHATSAPP');
  const conversationId = preknownConversationId ?? whatsappConv?.id;

  // Derive configurationId for call eligibility
  const configurationId = useMemo(() => {
    const fromConv = conversations.find((c) => c.configuration_id)?.configuration_id;
    if (fromConv) return fromConv;
    const calls = (contact as Record<string, unknown> | undefined)?.calls;
    if (Array.isArray(calls) && calls.length > 0) {
      return (calls[0] as { configuration_id?: string })?.configuration_id ?? undefined;
    }
    return undefined;
  }, [conversations, contact]);

  const { data: eligibility } = useCallEligibility(contactId, configurationId);
  const permissionMutation = useRequestWhatsAppPermission();
  const checkPermission = useCheckWhatsAppPermission();

  const { data: conversation } = useConversation(conversationId);
  const { data: lastCleared } = useToolWatchLastCleared(contactId);

  // Claim status
  const assignedUserId = conversation?.assigned_user_id ?? null;
  const assignedUserName = conversation?.assigned_user?.name ?? null;
  const isClaimedByMe = !!assignedUserId && assignedUserId === currentUser?.id;
  const isClaimedByOther = !!assignedUserId && assignedUserId !== currentUser?.id;

  const contactTags: string[] = (() => {
    if (Array.isArray(contact?.tags)) return contact.tags;
    if (initialTags) return initialTags;
    return [];
  })();

  // Viewport adjustment — runs before paint so the user never sees a mis-positioned menu
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let x = position.x;
    let y = position.y;
    if (x + rect.width > window.innerWidth) x = window.innerWidth - rect.width - 8;
    if (y + rect.height > window.innerHeight) y = window.innerHeight - rect.height - 8;
    if (x < 0) x = 8;
    if (y < 0) y = 8;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  }, [position, tagsExpanded]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleClaim = useCallback(() => {
    if (!conversationId) return;
    if (isClaimedByMe) {
      unclaimConversation.mutate(conversationId);
    } else {
      claimConversation.mutate({ conversationId, contactId });
    }
    onClose();
  }, [conversationId, contactId, isClaimedByMe, claimConversation, unclaimConversation, onClose]);

  const updateTags = useCallback(
    (updatedTags: string[]) => {
      if (!contact) return;
      updateContact.mutate({
        id: contactId,
        data: { tags: updatedTags },
      });
    },
    [contact, contactId, updateContact],
  );

  const handleAddTag = useCallback(() => {
    const tag = newTag.trim();
    if (!tag) return;
    if (contactTags.includes(tag)) {
      setNewTag('');
      return;
    }
    // A typed tag replaces the contact's existing tag of the same type.
    updateTags(mergeTagsWithTypes(contactTags, [tag], definitions));
    setNewTag('');
  }, [newTag, contactTags, updateTags, definitions]);

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      updateTags(contactTags.filter((t) => t !== tagToRemove));
    },
    [contactTags, updateTags],
  );

  const handleOpenOnPlatform = useCallback(() => {
    window.open(`/sales-console/${contactId}`, '_blank');
    onClose();
  }, [contactId, onClose]);

  // Build claim label based on current state
  let claimLabel = 'Claim conversation';
  if (isClaimedByMe) claimLabel = 'Unclaim conversation';
  else if (isClaimedByOther) claimLabel = `Claimed by ${assignedUserName ?? 'someone'} — Reclaim`;

  // Suggested tags: catalog tags first, then other tags in use — minus the
  // ones already on the contact.
  const suggestedTags = Array.from(
    new Set([...definitions.map((d) => d.name), ...(allTags ?? [])]),
  ).filter((t) => !contactTags.includes(t));

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="fixed z-[9999] max-w-[280px] min-w-[220px] rounded-lg border border-[#d1d7db] bg-white py-1 shadow-xl dark:border-[#2a3942] dark:bg-[#1a2730]"
      style={{ left: position.x, top: position.y }}
    >
      {/* Claim / Unclaim */}
      <button
        type="button"
        role="menuitem"
        disabled={!conversationId}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111b21] transition-colors hover:bg-[#f0f2f5] disabled:pointer-events-none disabled:opacity-40 dark:text-[#e9edef] dark:hover:bg-[#2a3942]"
        onClick={handleClaim}
      >
        <UserCheck className="h-4 w-4 shrink-0 text-[#00a884]" />
        <span className="truncate">{claimLabel}</span>
      </button>

      {/* Who last cleared this contact's alert */}
      {lastCleared && (
        <div
          className="flex items-center gap-2 px-3 py-1.5 text-xs text-[#667781] dark:text-[#8696a0]"
          title={`Alert: ${lastCleared.tool_name}`}
        >
          <BellOff className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{clearedByLabel(lastCleared)}</span>
        </div>
      )}

      {/* Call options */}
      {onCall &&
        (eligibility?.canCallPstn ||
          (eligibility?.pstnCooldownUntil &&
            new Date(eligibility.pstnCooldownUntil).getTime() > now)) && (
          <>
            <div className="mx-2 my-0.5 h-px bg-[#e4e6e9] dark:bg-[#2a3942]" />
            <button
              type="button"
              role="menuitem"
              disabled={!eligibility?.canCallPstn}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111b21] transition-colors hover:bg-[#f0f2f5] disabled:pointer-events-none disabled:opacity-40 dark:text-[#e9edef] dark:hover:bg-[#2a3942]"
              onClick={() => {
                onCall('pstn');
                onClose();
              }}
            >
              <Phone className="h-4 w-4 shrink-0 text-[#00a884]" />
              <span className="truncate">
                {eligibility?.canCallPstn ? 'Call on phone' : `Phone cooldown`}
              </span>
            </button>
          </>
        )}
      {onCall &&
        (() => {
          const waPermission = eligibility?.whatsappCallPermission ?? 'none';
          const hasCallPerm = waPermission === 'temporary' || waPermission === 'permanent';
          const needsRequest =
            waPermission === 'none' || waPermission === 'rejected' || waPermission === 'expired';
          const waiting = waPermission === 'requested';
          const showWhatsappOption =
            hasCallPerm ||
            needsRequest ||
            waiting ||
            (eligibility?.whatsappCooldownUntil &&
              new Date(eligibility.whatsappCooldownUntil).getTime() > now);

          if (!showWhatsappOption) return null;

          return (
            <>
              {!(eligibility?.canCallPstn || eligibility?.pstnCooldownUntil) && (
                <div className="mx-2 my-0.5 h-px bg-[#e4e6e9] dark:bg-[#2a3942]" />
              )}
              {hasCallPerm && (
                <button
                  type="button"
                  role="menuitem"
                  disabled={!eligibility?.canCallWhatsapp}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111b21] transition-colors hover:bg-[#f0f2f5] disabled:pointer-events-none disabled:opacity-40 dark:text-[#e9edef] dark:hover:bg-[#2a3942]"
                  onClick={() => {
                    onCall('whatsapp');
                    onClose();
                  }}
                >
                  <PhoneCall className="h-4 w-4 shrink-0 text-[#25d366]" />
                  <span className="truncate">Call on WhatsApp</span>
                </button>
              )}
              {needsRequest && (
                <button
                  type="button"
                  role="menuitem"
                  disabled={permissionMutation.isPending}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111b21] transition-colors hover:bg-[#f0f2f5] disabled:pointer-events-none disabled:opacity-40 dark:text-[#e9edef] dark:hover:bg-[#2a3942]"
                  onClick={() => {
                    if (configurationId) {
                      permissionMutation.mutate({ contactId, configurationId });
                    }
                    onClose();
                  }}
                >
                  <PhoneCall className="h-4 w-4 shrink-0 text-[#25d366]" />
                  <span className="truncate">Request WhatsApp call permission</span>
                </button>
              )}
              {waiting && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111b21] transition-colors hover:bg-[#f0f2f5] dark:text-[#e9edef] dark:hover:bg-[#2a3942]"
                    onClick={() => {
                      if (configurationId) {
                        checkPermission.mutate(
                          { contactId, configurationId },
                          { onSettled: () => onClose() },
                        );
                      }
                    }}
                  >
                    <PhoneCall className="h-4 w-4 shrink-0 text-[#25d366]" />
                    <span className="truncate">Check permission status</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#667781] transition-colors hover:bg-[#f0f2f5] dark:text-[#8696a0] dark:hover:bg-[#2a3942]"
                    onClick={() => {
                      if (configurationId) {
                        permissionMutation.mutate({ contactId, configurationId });
                        onClose();
                      }
                    }}
                  >
                    <Phone className="h-4 w-4 shrink-0 text-[#667781] dark:text-[#8696a0]" />
                    <span className="truncate">Resend permission request</span>
                  </button>
                </>
              )}
              {(needsRequest || waiting) && (
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#f59e0b] transition-colors hover:bg-[#f0f2f5] dark:hover:bg-[#2a3942]"
                  onClick={() => {
                    onCall('whatsapp', true);
                    onClose();
                  }}
                >
                  <PhoneCall className="h-4 w-4 shrink-0 text-[#f59e0b]" />
                  <span className="truncate">Call anyway (override)</span>
                </button>
              )}
            </>
          );
        })()}

      <div className="mx-2 my-0.5 h-px bg-[#e4e6e9] dark:bg-[#2a3942]" />

      {/* Tags toggle */}
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111b21] transition-colors hover:bg-[#f0f2f5] dark:text-[#e9edef] dark:hover:bg-[#2a3942]"
        onClick={() => {
          setTagsExpanded((prev) => !prev);
          if (!tagsExpanded) setTimeout(() => inputRef.current?.focus(), 50);
        }}
      >
        <Tag className="h-4 w-4 shrink-0 text-[#667781] dark:text-[#8696a0]" />
        <span className="flex-1">Tags</span>
        {contactTags.length > 0 && (
          <span className="mr-1 text-[10px] text-[#667781] dark:text-[#8696a0]">
            {contactTags.length}
          </span>
        )}
        <span className="text-[10px] text-[#667781] dark:text-[#8696a0]">
          {tagsExpanded ? '▾' : '▸'}
        </span>
      </button>

      {tagsExpanded && (
        <div className="max-h-[200px] overflow-y-auto border-t border-[#d1d7db] px-3 py-2 dark:border-[#2a3942]">
          {/* Add-tag input */}
          <input
            ref={inputRef}
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
              // Let Escape bubble so the document handler closes the menu
              if (e.key !== 'Escape') e.stopPropagation();
            }}
            placeholder="Add tag..."
            className="h-7 w-full rounded border border-[#d1d7db] bg-[#f0f2f5] px-2 text-xs text-[#111b21] placeholder-[#667781] outline-none focus:border-[#00a884] dark:border-[#2a3942] dark:bg-[#0b141a] dark:text-[#e9edef] dark:placeholder-[#8696a0]"
          />

          {/* Current tags */}
          {contactTags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {contactTags.map((tag) => (
                <TagBadge
                  key={tag}
                  name={tag}
                  definition={byName.get(tag)}
                  onRemove={() => handleRemoveTag(tag)}
                />
              ))}
            </div>
          )}

          {/* Suggested tags from the catalog + tags in use */}
          {suggestedTags.length > 0 && newTag === '' && (
            <div className="mt-2">
              <span className="text-[10px] text-[#667781] dark:text-[#8696a0]">Suggestions</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {suggestedTags.slice(0, 8).map((tag) => {
                  const def = byName.get(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() =>
                        updateTags(mergeTagsWithTypes(contactTags, [tag], definitions))
                      }
                      className="cursor-pointer rounded-full border border-dashed px-2 py-0.5 text-[11px] transition-colors"
                      style={
                        def?.color
                          ? { borderColor: def.color, color: def.color }
                          : { borderColor: '#8696a0', color: '#8696a0' }
                      }
                      title={def?.type?.trim() ? `Type: ${def.type.trim()}` : undefined}
                    >
                      + {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mx-2 my-0.5 h-px bg-[#e4e6e9] dark:bg-[#2a3942]" />

      {/* Open on platform */}
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111b21] transition-colors hover:bg-[#f0f2f5] dark:text-[#e9edef] dark:hover:bg-[#2a3942]"
        onClick={handleOpenOnPlatform}
      >
        <ExternalLink className="h-4 w-4 shrink-0 text-[#667781] dark:text-[#8696a0]" />
        <span>Open on platform</span>
      </button>

      {/* Edit / delete — hosts that own the dialogs opt in */}
      {(onEdit || onDeleteContact) && (
        <div className="mx-2 my-0.5 h-px bg-[#e4e6e9] dark:bg-[#2a3942]" />
      )}
      {onEdit && (
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111b21] transition-colors hover:bg-[#f0f2f5] dark:text-[#e9edef] dark:hover:bg-[#2a3942]"
          onClick={() => {
            onEdit();
            onClose();
          }}
        >
          <Pencil className="h-4 w-4 shrink-0 text-[#667781] dark:text-[#8696a0]" />
          <span>Edit contact</span>
        </button>
      )}
      {onDeleteContact && (
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-500 transition-colors hover:bg-[#f0f2f5] dark:text-red-400 dark:hover:bg-[#2a3942]"
          onClick={() => {
            onDeleteContact();
            onClose();
          }}
        >
          <Trash2 className="h-4 w-4 shrink-0" />
          <span>Delete contact…</span>
        </button>
      )}
    </div>,
    document.body,
  );
};
